import { ArrowDown, ArrowUp, Copy, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PropertySchemaType, type PropertySchemaType as PropertySchemaTypeValue } from "~/common/types";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "~/components/ui";
import type { PropertySchemaAssociation_CategoryFragment } from "~/features/admin/categories";
import {
  isEntityInstance,
  isRelationInstance,
  type SchemaDesignerCategory,
  type SchemaDesignerCategoryCardinality,
  type SchemaDesignerCategoryInstance,
  type SheetColumn,
  type SheetState,
  type SheetVariantKeys,
  schemaDesignerCategoryCardinalities,
  VariantKeySlots,
  type VariantKeyValidationError,
} from "~/features/dataImportProjects/hooks";

const NO_VALUE = "__none__";
const PROPERTY_SCHEMA_TYPES = Object.values(PropertySchemaType);
const SELECT_PROPERTY_TYPES: string[] = [PropertySchemaType.Select, PropertySchemaType.MultiSelect];

function toSelectOptionsText(selectOptions: unknown): string {
  if (!selectOptions || typeof selectOptions !== "object" || Array.isArray(selectOptions)) return "";

  return Object.entries(selectOptions)
    .map(([value, label]) => {
      const labelText = label && typeof label === "object" && "en" in label ? String(label.en) : String(label ?? value);
      return `${value}:${labelText}`;
    })
    .join("\n");
}

function parseSelectOptionsText(text: string): Array<{ label: string; value: string }> {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(":");
      if (separatorIndex === -1) return { value: line, label: line };
      const value = line.slice(0, separatorIndex).trim();
      const label = line.slice(separatorIndex + 1).trim();
      return { value, label: label || value };
    })
    .filter((option) => option.value);
}

function CategoryBadge({ category }: { category: SchemaDesignerCategoryInstance }) {
  return (
    <Badge tone={category.category.type === "entity" ? "primary" : "status-review"}>{category.category.type}</Badge>
  );
}

export function AvailableCategoryList({
  title,
  categories,
  loading,
  onSearch,
  onAdd,
}: {
  title: string;
  categories: SchemaDesignerCategory[];
  loading: boolean;
  onSearch: (value: string) => void;
  onAdd: (category: SchemaDesignerCategory) => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-14-bold text-fg-1">{title}</h3>
        {loading ? <span className="text-12-regular text-fg-3">{t("ui.loading")}...</span> : null}
      </div>
      <Input placeholder={t("ui.search")} onChange={(event) => onSearch(event.target.value)} />
      <div className="max-h-[360px] overflow-auto rounded-[var(--radius-md)] border border-border-default">
        {categories.length === 0 ? (
          <p className="p-4 text-12-regular text-fg-3">{t("ui.noResultsFound")}</p>
        ) : (
          <ul className="divide-y divide-border-default">
            {categories.map((category) => (
              <li key={category.id} className="flex items-start justify-between gap-3 p-3">
                <div className="min-w-0">
                  <p className="truncate text-14-bold text-fg-1">{category.name}</p>
                  <p className="text-12-regular text-fg-3">
                    {category.properties.length} {t("feature.dataSourceSchemaDesigner.properties")}
                  </p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => onAdd(category)}>
                  <Plus className="size-3.5" />
                  {t("ui.add")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SelectedCategoryCard({
  category,
  entityCategories,
  onRemove,
  onSetMain,
  onRename,
  onUpdateRelationSourceTarget,
  onUpdateRelationCardinality,
}: {
  category: SchemaDesignerCategoryInstance;
  entityCategories: SchemaDesignerCategoryInstance[];
  onRemove: (instanceId: string) => void;
  onSetMain: (instanceId: string) => void;
  onRename: (instanceId: string, label: string) => void;
  onUpdateRelationSourceTarget: (
    instanceId: string,
    sourceEntityId: string | null,
    targetEntityId: string | null,
  ) => void;
  onUpdateRelationCardinality: (instanceId: string, cardinality: SchemaDesignerCategoryCardinality) => void;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(category.instanceLabel);
  const sourceValue = isRelationInstance(category) ? (category.sourceCategory?.instanceId ?? NO_VALUE) : NO_VALUE;
  const targetValue = isRelationInstance(category) ? (category.targetCategory?.instanceId ?? NO_VALUE) : NO_VALUE;

  useEffect(() => setLabel(category.instanceLabel), [category.instanceLabel]);

  return (
    <Card className="gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryBadge category={category} />
            {category.isMain ? (
              <Badge tone="score-a">{t("feature.dataSourceSchemaDesigner.mainCategory")}</Badge>
            ) : null}
            <p className="truncate text-14-bold text-fg-1">{category.category.name}</p>
          </div>
          <p className="text-12-regular text-fg-3">{category.instanceId}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isEntityInstance(category) ? (
            <Button type="button" size="sm" variant="outline" onClick={() => onSetMain(category.instanceId)}>
              {t("feature.dataSourceSchemaDesigner.setAsMain")}
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="outline" onClick={() => onRemove(category.instanceId)}>
            <Trash2 className="size-3.5" />
            {t("ui.remove")}
          </Button>
        </div>
      </div>
      <div className="grid gap-2">
        <Label>{t("feature.dataSourceSchemaDesigner.categoryName")}</Label>
        <Input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onBlur={() => onRename(category.instanceId, label)}
        />
      </div>
      {isRelationInstance(category) ? (
        <div className="grid gap-3 md:grid-cols-3">
          <RelationEndpointSelect
            label={t("feature.dataSourceSchemaDesigner.sourceEntity")}
            value={sourceValue}
            entityCategories={entityCategories}
            onChange={(value) =>
              onUpdateRelationSourceTarget(
                category.instanceId,
                value === NO_VALUE ? null : value,
                targetValue === NO_VALUE ? null : targetValue,
              )
            }
          />
          <RelationEndpointSelect
            label={t("feature.dataSourceSchemaDesigner.targetEntity")}
            value={targetValue}
            entityCategories={entityCategories}
            onChange={(value) =>
              onUpdateRelationSourceTarget(
                category.instanceId,
                sourceValue === NO_VALUE ? null : sourceValue,
                value === NO_VALUE ? null : value,
              )
            }
          />
          <div className="grid gap-2">
            <Label>{t("feature.dataSourceSchemaDesigner.cardinality")}</Label>
            <Select
              value={category.cardinality}
              onValueChange={(value) =>
                onUpdateRelationCardinality(category.instanceId, value as SchemaDesignerCategoryCardinality)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(schemaDesignerCategoryCardinalities).map((cardinality) => (
                  <SelectItem key={cardinality} value={cardinality}>
                    {cardinality}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function RelationEndpointSelect({
  label,
  value,
  entityCategories,
  onChange,
}: {
  label: string;
  value: string;
  entityCategories: SchemaDesignerCategoryInstance[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_VALUE}>-</SelectItem>
          {entityCategories.map((entity) => (
            <SelectItem key={entity.instanceId} value={entity.instanceId}>
              {entity.instanceLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SelectedCategoriesPanel({
  selectedCategories,
  onRemove,
  onSetMain,
  onRename,
  onUpdatePlan,
  onUpdateRelationSourceTarget,
  onUpdateRelationCardinality,
}: {
  selectedCategories: SchemaDesignerCategoryInstance[];
  onRemove: (instanceId: string) => void;
  onSetMain: (instanceId: string) => void;
  onRename: (instanceId: string, label: string) => void;
  onUpdatePlan: () => void;
  onUpdateRelationSourceTarget: (
    instanceId: string,
    sourceEntityId: string | null,
    targetEntityId: string | null,
  ) => void;
  onUpdateRelationCardinality: (instanceId: string, cardinality: SchemaDesignerCategoryCardinality) => void;
}) {
  const { t } = useTranslation();
  const entityCategories = selectedCategories.filter(isEntityInstance);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{t("feature.dataSourceSchemaDesigner.selectedCategories")}</CardTitle>
            <CardDescription>
              {selectedCategories.length} {t("model.category.modelNames")}
            </CardDescription>
          </div>
          <Button type="button" size="sm" onClick={onUpdatePlan} disabled={selectedCategories.length === 0}>
            <RefreshCw className="size-3.5" />
            {t("feature.dataSourceSchemaDesigner.updatePlan")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {selectedCategories.length === 0 ? (
          <div className="rounded-[var(--radius-md)] border border-border-default p-4">
            <p className="text-14-bold text-fg-1">{t("feature.dataSourceSchemaDesigner.noCategoriesSelected")}</p>
            <p className="mt-1 text-12-regular text-fg-3">
              {t("feature.dataSourceSchemaDesigner.selectCategoriesBelow")}
            </p>
          </div>
        ) : (
          <div className="grid gap-3">
            {selectedCategories.map((category) => (
              <SelectedCategoryCard
                key={category.instanceId}
                category={category}
                entityCategories={entityCategories}
                onRemove={onRemove}
                onSetMain={onSetMain}
                onRename={onRename}
                onUpdateRelationSourceTarget={onUpdateRelationSourceTarget}
                onUpdateRelationCardinality={onUpdateRelationCardinality}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ColumnRow({
  column,
  selectedCategories,
  onUpdateColumnLabel,
  onUpdateColumnDescription,
  onUpdateColumnSchemaType,
  onToggleColumnRequired,
  onMoveColumn,
  onRemoveColumn,
  onDuplicateColumn,
  onUpdateColumnProperty,
}: {
  column: SheetColumn;
  selectedCategories: readonly SchemaDesignerCategoryInstance[];
  onUpdateColumnLabel: (columnId: string, label: string) => void;
  onUpdateColumnDescription: (columnId: string, description: string) => void;
  onUpdateColumnSchemaType: (
    columnId: string,
    type: PropertySchemaTypeValue,
    selectOptions?: Array<{ label: string; value: string }>,
  ) => void;
  onToggleColumnRequired: (columnId: string) => void;
  onMoveColumn: (columnId: string, direction: "up" | "down") => void;
  onRemoveColumn: (columnId: string) => void;
  onDuplicateColumn: (columnId: string) => void;
  onUpdateColumnProperty: (columnId: string, association: PropertySchemaAssociation_CategoryFragment | null) => void;
}) {
  const { t } = useTranslation();
  const category = selectedCategories.find((cat) => cat.instanceId === column.categoryInstanceId);
  const properties = category?.category.properties ?? [];
  const [columnLabel, setColumnLabel] = useState(column.columnLabel);
  const [columnLabelError, setColumnLabelError] = useState<string | null>(null);
  const [selectOptionsText, setSelectOptionsText] = useState(
    toSelectOptionsText(column.property.propertySchema.selectOptions),
  );
  const isSelectType = SELECT_PROPERTY_TYPES.includes(column.property.propertySchema.propertyTypeId);

  useEffect(() => setColumnLabel(column.columnLabel), [column.columnLabel]);
  useEffect(
    () => setSelectOptionsText(toSelectOptionsText(column.property.propertySchema.selectOptions)),
    [column.property.propertySchema.selectOptions],
  );

  const commitColumnLabel = () => {
    if (columnLabel === column.columnLabel) return;
    try {
      onUpdateColumnLabel(column.columnId, columnLabel);
      setColumnLabelError(null);
    } catch (error) {
      setColumnLabelError(error instanceof Error ? error.message : String(error));
      setColumnLabel(column.columnLabel);
    }
  };

  const updateColumnSchemaType = (type: PropertySchemaTypeValue, optionsText = selectOptionsText) => {
    const selectOptions = SELECT_PROPERTY_TYPES.includes(type) ? parseSelectOptionsText(optionsText) : undefined;
    onUpdateColumnSchemaType(column.columnId, type, selectOptions);
  };

  return (
    <tr className="border-t border-border-default align-top first:border-t-0">
      <td className="px-3 py-2 text-fg-3">{column.order + 1}</td>
      <td className="min-w-[180px] px-3 py-2">
        <Input
          value={columnLabel}
          onChange={(event) => setColumnLabel(event.target.value)}
          onBlur={commitColumnLabel}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.currentTarget.blur();
            }
          }}
        />
        {columnLabelError ? <p className="mt-1 text-12-regular text-danger">{columnLabelError}</p> : null}
      </td>
      <td className="min-w-[170px] px-3 py-2">
        <Badge
          tone={
            column.columnKind === "foreignKey"
              ? "status-review"
              : column.columnKind === "custom"
                ? "score-a"
                : "default"
          }
        >
          {column.columnKind}
        </Badge>
        <p className="mt-1 text-12-regular text-fg-3">{column.categoryInstanceLabel || "-"}</p>
      </td>
      <td className="min-w-[170px] px-3 py-2">
        <Select
          value={column.property.propertySchema.propertyTypeId}
          disabled={column.columnKind === "foreignKey"}
          onValueChange={(value) => updateColumnSchemaType(value as PropertySchemaTypeValue)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PROPERTY_SCHEMA_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isSelectType ? (
          <Textarea
            className="mt-2"
            rows={3}
            value={selectOptionsText}
            placeholder="value:label"
            disabled={column.columnKind === "foreignKey"}
            onChange={(event) => setSelectOptionsText(event.target.value)}
            onBlur={() => updateColumnSchemaType(column.property.propertySchema.propertyTypeId, selectOptionsText)}
          />
        ) : null}
      </td>
      <td className="min-w-[180px] px-3 py-2">
        <Select
          value={column.property.name || NO_VALUE}
          disabled={column.columnKind === "foreignKey" || properties.length === 0}
          onValueChange={(value) => {
            if (value === NO_VALUE) {
              onUpdateColumnProperty(column.columnId, null);
              return;
            }
            onUpdateColumnProperty(column.columnId, properties.find((property) => property.name === value) ?? null);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="-" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_VALUE}>-</SelectItem>
            {properties.map((property) => (
              <SelectItem key={property.id} value={property.name}>
                {property.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="min-w-[220px] px-3 py-2">
        <Textarea
          rows={2}
          value={column.property.description ?? ""}
          disabled={column.columnKind === "foreignKey"}
          onChange={(event) => onUpdateColumnDescription(column.columnId, event.target.value)}
        />
      </td>
      <td className="px-3 py-2">
        <Checkbox
          checked={column.property.required}
          disabled={column.columnKind === "foreignKey"}
          aria-label={t("model.propertySchemaAssociation.required")}
          onCheckedChange={() => onToggleColumnRequired(column.columnId)}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          <Button type="button" variant="outline" size="icon" onClick={() => onMoveColumn(column.columnId, "up")}>
            <ArrowUp className="size-3.5" />
          </Button>
          <Button type="button" variant="outline" size="icon" onClick={() => onMoveColumn(column.columnId, "down")}>
            <ArrowDown className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={column.columnKind === "foreignKey"}
            onClick={() => onDuplicateColumn(column.columnId)}
          >
            <Copy className="size-3.5" />
          </Button>
          <Button type="button" variant="outline" size="icon" onClick={() => onRemoveColumn(column.columnId)}>
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function variantKeyErrorText(t: ReturnType<typeof useTranslation>["t"], error: VariantKeyValidationError): string {
  switch (error.reason) {
    case "incomplete":
      return t("feature.dataSourceSchemaDesigner.variantKeys.errorIncomplete", {
        categoryName: error.categoryName,
      });
    case "invalidCharset":
      return t("feature.dataSourceSchemaDesigner.variantKeys.errorInvalidCharset", {
        categoryName: error.categoryName,
      });
    case "duplicateKey":
      return t("feature.dataSourceSchemaDesigner.variantKeys.errorDuplicateKey", {
        categoryName: error.categoryName,
      });
  }
}

/**
 * Per-slot variantKey inputs for one sheet, shown only when the sheet maps
 * the same category through more than one instance. Distinct keys keep those
 * instances as separate composers (and records); leaving all inputs empty
 * collapses them into one record, which is the pre-variantKey behavior.
 */
function VariantKeySection({
  sheet,
  variantKeys,
  errors,
  onUpdateVariantKey,
}: {
  sheet: SheetState;
  variantKeys: SheetVariantKeys;
  errors: VariantKeyValidationError[];
  onUpdateVariantKey: (sheetName: string, instanceId: string, variantKey: string) => void;
}) {
  const { t } = useTranslation();
  const slotGroups = VariantKeySlots.duplicatedCategorySlots(sheet);
  if (slotGroups.length === 0) {
    return null;
  }
  const sheetErrors = errors.filter((error) => error.sheetName === sheet.name);

  return (
    <div className="mb-4 rounded-[var(--radius-md)] border border-border-default p-4">
      <p className="text-14-bold text-fg-1">{t("feature.dataSourceSchemaDesigner.variantKeys.title")}</p>
      <p className="mt-1 text-12-regular text-fg-3">{t("feature.dataSourceSchemaDesigner.variantKeys.hint")}</p>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {slotGroups.flatMap((group) =>
          group.instances.map((instance) => (
            <div key={instance.instanceId} className="grid gap-2">
              <Label htmlFor={`variant-key-${instance.instanceId}`}>
                {instance.instanceLabel} ({group.categoryName})
              </Label>
              <Input
                id={`variant-key-${instance.instanceId}`}
                value={variantKeys[sheet.name]?.[instance.instanceId] ?? ""}
                placeholder={t("feature.dataSourceSchemaDesigner.variantKeys.placeholder")}
                onChange={(event) => onUpdateVariantKey(sheet.name, instance.instanceId, event.target.value)}
              />
            </div>
          )),
        )}
      </div>
      {sheetErrors.length > 0 ? (
        <ul className="mt-3 grid gap-1">
          {sheetErrors.map((error) => (
            <li key={`${error.categoryName}-${error.reason}`} className="text-12-regular text-danger">
              {variantKeyErrorText(t, error)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function SheetPanel({
  sheets,
  activeSheetName,
  selectedCategories,
  variantKeys,
  variantKeyErrors,
  onSheetChange,
  onUpdateVariantKey,
  onAddCustomColumn,
  onUpdateColumnLabel,
  onUpdateColumnDescription,
  onUpdateColumnSchemaType,
  onToggleColumnRequired,
  onMoveColumn,
  onRemoveColumn,
  onDuplicateColumn,
  onUpdateColumnProperty,
}: {
  sheets: SheetState[];
  activeSheetName: string;
  selectedCategories: readonly SchemaDesignerCategoryInstance[];
  variantKeys: SheetVariantKeys;
  variantKeyErrors: VariantKeyValidationError[];
  onSheetChange: (sheetName: string) => void;
  onUpdateVariantKey: (sheetName: string, instanceId: string, variantKey: string) => void;
  onAddCustomColumn: () => void;
  onUpdateColumnLabel: (columnId: string, label: string) => void;
  onUpdateColumnDescription: (columnId: string, description: string) => void;
  onUpdateColumnSchemaType: (
    columnId: string,
    type: PropertySchemaTypeValue,
    selectOptions?: Array<{ label: string; value: string }>,
  ) => void;
  onToggleColumnRequired: (columnId: string) => void;
  onMoveColumn: (columnId: string, direction: "up" | "down") => void;
  onRemoveColumn: (columnId: string) => void;
  onDuplicateColumn: (columnId: string) => void;
  onUpdateColumnProperty: (columnId: string, association: PropertySchemaAssociation_CategoryFragment | null) => void;
}) {
  const { t } = useTranslation();
  const activeSheet = sheets.find((sheet) => sheet.name === activeSheetName) ?? sheets[0];

  useEffect(() => {
    if (!activeSheetName && sheets[0]) onSheetChange(sheets[0].name);
  }, [activeSheetName, onSheetChange, sheets]);

  if (!activeSheet) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("feature.dataSourceSchemaDesigner.columnPreview")}</CardTitle>
          <CardDescription>{t("feature.dataSourceSchemaDesigner.selectCategoriesToAddColumns")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{t("feature.dataSourceSchemaDesigner.columnDefinition")}</CardTitle>
            <CardDescription>
              {activeSheet.name} / {activeSheet.columns.length} {t("feature.dataSourceSchemaDesigner.columns")}
            </CardDescription>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={onAddCustomColumn}>
            <Plus className="size-3.5" />
            {t("feature.dataSourceSchemaDesigner.customColumn")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeSheet.name} onValueChange={onSheetChange}>
          <TabsList className="max-w-full overflow-x-auto">
            {sheets.map((sheet) => (
              <TabsTrigger key={sheet.name} value={sheet.name}>
                {sheet.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {sheets.map((sheet) => (
            <TabsContent key={sheet.name} value={sheet.name} className="pt-4">
              <VariantKeySection
                sheet={sheet}
                variantKeys={variantKeys}
                errors={variantKeyErrors}
                onUpdateVariantKey={onUpdateVariantKey}
              />
              <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
                <table className="w-full min-w-[1180px] border-collapse text-12-regular">
                  <thead className="bg-bg-subtle text-fg-2">
                    <tr>
                      {[
                        t("ui.order"),
                        t("model.common.name"),
                        t("ui.type"),
                        t("model.propertySchema.propertyType"),
                        t("feature.dataSourceSchemaDesigner.propertyMapping"),
                        t("model.common.description"),
                        t("model.propertySchemaAssociation.required"),
                        t("ui.actions"),
                      ].map((label) => (
                        <th key={label} className="border-b border-border-default px-3 py-2 text-left font-semibold">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.columns.map((column) => (
                      <ColumnRow
                        key={column.columnId}
                        column={column}
                        selectedCategories={selectedCategories}
                        onUpdateColumnLabel={onUpdateColumnLabel}
                        onUpdateColumnDescription={onUpdateColumnDescription}
                        onUpdateColumnSchemaType={onUpdateColumnSchemaType}
                        onToggleColumnRequired={onToggleColumnRequired}
                        onMoveColumn={onMoveColumn}
                        onRemoveColumn={onRemoveColumn}
                        onDuplicateColumn={onDuplicateColumn}
                        onUpdateColumnProperty={onUpdateColumnProperty}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

export function UnconnectedCategoriesDialog({
  open,
  categories,
  onClose,
}: {
  open: boolean;
  categories: Array<{ id: string; name: string }>;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("feature.dataSourceSchemaDesigner.unconnectedCategoriesTitle")}</DialogTitle>
        </DialogHeader>
        <div className="rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-4 text-12-regular text-fg-2">
          {t("feature.dataSourceSchemaDesigner.unconnectedCategoriesWarning")}
        </div>
        <ul className="grid gap-2">
          {categories.map((category) => (
            <li
              key={category.id}
              className="rounded-[var(--radius-md)] border border-border-default p-3 text-14-regular"
            >
              {category.name}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t("ui.ok")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
